import React, { useEffect, useRef, useState } from "react";
import Deck from "./Deck";
import TrackDrawer from "./TrackDrawer";
import { Zap, Monitor, Youtube, X } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { playlist as playlistApi, sessions as sessionsApi } from "../lib/api";

// Wake Lock helper
function useWakeLock(active) {
  const wakeRef = useRef(null);
  useEffect(() => {
    let released = false;
    async function acquire() {
      try {
        if ("wakeLock" in navigator) {
          wakeRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (_) {}
    }
    async function release() {
      try {
        wakeRef.current && (await wakeRef.current.release());
        wakeRef.current = null;
      } catch (_) {}
    }
    if (active) acquire();
    const handleVis = () => {
      if (active && document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", handleVis);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", handleVis);
      release();
    };
  }, [active]);
}

// Desktop Emulation: forces desktop viewport so mobile browsers relax
// single-audio-source policies and both YT iframes can play simultaneously.
function useDesktopEmulation(active) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute("content") || "";
    if (active) {
      meta.setAttribute("content", "width=1440, initial-scale=0.35, user-scalable=yes");
      document.documentElement.setAttribute("data-desktop-emu", "1");
    } else {
      meta.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
      );
      document.documentElement.removeAttribute("data-desktop-emu");
    }
    return () => {
      meta.setAttribute("content", original);
      document.documentElement.removeAttribute("data-desktop-emu");
    };
  }, [active]);
}

// Keep-alive: silent oscillator + audio context prevents Chrome/mobile
// from suspending background media playback when the screen locks.
function useBackgroundKeepAlive(active) {
  useEffect(() => {
    if (!active) return;
    let ctx, osc, gain;
    let silentAudio;
    const start = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        ctx = new AC();
        osc = ctx.createOscillator();
        gain = ctx.createGain();
        gain.gain.value = 0.0001; // effectively silent, but keeps audio session alive
        osc.frequency.value = 20;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
      } catch (_) {}

      // Second layer of anti-throttling: a real HTMLMediaElement playing
      // silent audio. Browsers (Chrome especially) use HTMLMediaElement
      // playback as the strongest signal of "this tab is producing audio,
      // don't throttle timers or postMessage on it." AudioContext alone
      // is sometimes ignored when the screen is locked; a real <audio>
      // element is not.
      try {
        if (!silentAudio) {
          silentAudio = document.createElement("audio");
          // 0.1s of true PCM silence, encoded as base64 WAV.
          silentAudio.src =
            "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
          silentAudio.loop = true;
          silentAudio.preload = "auto";
          silentAudio.volume = 0.001; // effectively inaudible
          silentAudio.setAttribute("playsinline", "");
          silentAudio.setAttribute("aria-hidden", "true");
          silentAudio.style.position = "absolute";
          silentAudio.style.width = "1px";
          silentAudio.style.height = "1px";
          silentAudio.style.opacity = "0";
          silentAudio.style.pointerEvents = "none";
          document.body.appendChild(silentAudio);
        }
        const p = silentAudio.play();
        if (p && p.catch) p.catch(() => {});
      } catch (_) {}
    };
    const resume = () => {
      try {
        if (ctx && ctx.state === "suspended") ctx.resume();
      } catch (_) {}
      try {
        if (silentAudio && silentAudio.paused) {
          const p = silentAudio.play();
          if (p && p.catch) p.catch(() => {});
        }
      } catch (_) {}
    };
    // AudioContext + <audio>.play() may need a user gesture — attach one-time unlock handlers
    const unlock = () => {
      if (!ctx) start();
      else resume();
      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("keydown", unlock);
    };
    document.addEventListener("click", unlock);
    document.addEventListener("touchstart", unlock);
    document.addEventListener("keydown", unlock);

    const onVis = () => resume();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("keydown", unlock);
      document.removeEventListener("visibilitychange", onVis);
      try { osc && osc.stop(); } catch (_) {}
      try { ctx && ctx.close(); } catch (_) {}
      try {
        if (silentAudio) {
          silentAudio.pause();
          silentAudio.remove();
        }
      } catch (_) {}
    };
  }, [active]);
}

// Wake Lock: requests the OS to keep the screen from sleeping while the
// DJ tab is visible and Auto-DJ is engaged. Auto-released when the tab
// hides so we don't drain battery unnecessarily; re-acquired when the
// tab becomes visible again.
// (Implemented above at the top of this file as useWakeLock(active).)

// MediaSession: registers active playback with the OS so Chrome / mobile
// won't throttle audio in the background and shows track metadata on lock screen.
// Also wires next/previous hardware/lock-screen buttons to queue advance.
function useMediaSession({ deckA, deckB, fader, deckARef, deckBRef, onNext, onPrev }) {
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const track = fader < 0.5 ? deckA : deckB;
    if (!track) {
      try { navigator.mediaSession.metadata = null; } catch (_) {}
      return;
    }
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: track.title || "Untitled",
        artist: track.channel || "House Party",
        album: "House Party DJ",
        artwork: [
          { src: track.thumbnail || "", sizes: "480x360", type: "image/jpeg" },
        ],
      });
      const primaryRef = fader < 0.5 ? deckARef : deckBRef;
      navigator.mediaSession.setActionHandler("play", () => {
        try { primaryRef.current && primaryRef.current.play(); } catch (_) {}
      });
      navigator.mediaSession.setActionHandler("pause", () => {
        try { primaryRef.current && primaryRef.current.pause(); } catch (_) {}
      });
      navigator.mediaSession.setActionHandler("nexttrack", () => {
        try { onNext && onNext(); } catch (_) {}
      });
      navigator.mediaSession.setActionHandler("previoustrack", () => {
        try { onPrev && onPrev(); } catch (_) {}
      });
      navigator.mediaSession.playbackState = "playing";
    } catch (_) {}
  }, [deckA, deckB, fader, deckARef, deckBRef, onNext, onPrev]);
}

export default function DJConsole({ tracks, deckA, setDeckA, deckB, setDeckB, onLoadDeck, refreshPlaylist }) {
  const [fader, setFader] = useState(0.5); // 0=A only, 1=B only
  const [autoDJ, setAutoDJ] = useState(false);
  const [wakeOn, setWakeOn] = useState(true);
  const [desktopMode, setDesktopMode] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = localStorage.getItem("desktopMode");
    if (saved !== null) return saved === "1";
    // First visit: auto-enable if mobile UA
    const ua = navigator.userAgent || "";
    const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
    if (isMobile) {
      localStorage.setItem("desktopMode", "1");
      return true;
    }
    return false;
  });
  const [dismissedYtBanner, setDismissedYtBanner] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("ytBannerDismissed") === "1"
  );
  const deckARef = useRef(null);
  const deckBRef = useRef(null);
  const autoTransitioning = useRef(false);

  useWakeLock(wakeOn);
  useDesktopEmulation(desktopMode);
  useBackgroundKeepAlive(!!(deckA || deckB));

  // Advance track on the primary (loudest) deck; if fader is exactly middle, prefer A
  const advance = (delta) => {
    if (!tracks || tracks.length === 0) return;
    const primarySide = fader < 0.5 ? "A" : "B";
    const current = primarySide === "A" ? deckA : deckB;
    const setter = primarySide === "A" ? setDeckA : setDeckB;
    let idx = current ? tracks.findIndex((t) => t.video_id === current.video_id) : -1;
    if (idx < 0) idx = 0;
    else idx = (idx + delta + tracks.length) % tracks.length;
    const next = tracks[idx];
    if (!next) return;
    setter(next);
    toast(`${delta > 0 ? "Next" : "Previous"}: ${next.title}`, { duration: 2200 });
  };
  const onMediaNext = () => advance(1);
  const onMediaPrev = () => advance(-1);

  useMediaSession({
    deckA,
    deckB,
    fader,
    deckARef,
    deckBRef,
    onNext: onMediaNext,
    onPrev: onMediaPrev,
  });

  useEffect(() => {
    if (desktopMode) localStorage.setItem("desktopMode", "1");
    else localStorage.setItem("desktopMode", "0");
  }, [desktopMode]);

  const dismissYtBanner = () => {
    localStorage.setItem("ytBannerDismissed", "1");
    setDismissedYtBanner(true);
  };

  // Heartbeat every 30s while console mounted
  const sessionIdRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const beat = async () => {
      try {
        const r = await sessionsApi.heartbeat(sessionIdRef.current);
        if (!cancelled) sessionIdRef.current = r.session_id;
      } catch (_) {}
    };
    beat();
    const iv = setInterval(beat, 30000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);

  const volA = 1 - fader; // 0..1
  const volB = fader;

  const handleBpmChange = async (song_uid, bpm) => {
    try {
      await playlistApi.updateBpm(song_uid, bpm);
      // Update the loaded decks if this song matches
      if (deckA?.song_uid === song_uid) setDeckA({ ...deckA, bpm, bpm_manual: true });
      if (deckB?.song_uid === song_uid) setDeckB({ ...deckB, bpm, bpm_manual: true });
      refreshPlaylist && refreshPlaylist();
    } catch (e) {
      // ignore
    }
  };

  const handleMarkDropEnd = async (song_uid, seconds) => {
    try {
      await playlistApi.markDropEnd(song_uid, seconds);
      if (deckA?.song_uid === song_uid) setDeckA({ ...deckA, drop_end_seconds: seconds });
      if (deckB?.song_uid === song_uid) setDeckB({ ...deckB, drop_end_seconds: seconds });
      const m = Math.floor(seconds / 60);
      const s = String(Math.floor(seconds % 60)).padStart(2, "0");
      toast.success(`Drop-end marked at ${m}:${s} — Auto-DJ will respect it.`);
      refreshPlaylist && refreshPlaylist();
    } catch (e) {
      toast.error("Failed to save drop marker");
    }
  };

  const pickNext = (currentSide) => {
    if (!tracks || tracks.length === 0) return null;
    const current = currentSide === "A" ? deckA : deckB;
    if (!current) return tracks[0];
    const idx = tracks.findIndex((t) => t.video_id === current.video_id);
    const nextIdx = idx >= 0 ? (idx + 1) % tracks.length : 0;
    return tracks[nextIdx];
  };

  const handleNearEnd = (side, secondsLeft) => {
    if (!autoDJ || autoTransitioning.current) return;

    // Sanity check: only accept a near-end trigger from a deck that is
    // actually playing (state 1) or buffering (3). Prevents phantom
    // triggers when a freshly-cued deck reports weird transitional
    // timing values that momentarily satisfy d - t <= 20.
    const callingRef = side === "A" ? deckARef : deckBRef;
    if (callingRef.current && callingRef.current.getPlayerState) {
      const st = callingRef.current.getPlayerState();
      if (st !== 1 && st !== 3) return;
    }

    autoTransitioning.current = true;

    const otherSide = side === "A" ? "B" : "A";
    const otherRef = otherSide === "A" ? deckARef : deckBRef;
    const currentRef = side === "A" ? deckARef : deckBRef;
    const otherSetter = otherSide === "A" ? setDeckA : setDeckB;
    const next = pickNext(side);
    if (!next) { autoTransitioning.current = false; return; }

    const currentTrack = side === "A" ? deckA : deckB;

    // If the DJ has marked a drop-end on the current track, wait until we're
    // safely past the drop before transitioning. Give the drop 3s of tail room.
    let extraWaitMs = 0;
    const dropEnd = currentTrack?.drop_end_seconds;
    if (dropEnd != null && currentRef.current && currentRef.current.getCurrentTime) {
      const now = currentRef.current.getCurrentTime() || 0;
      const dropTail = dropEnd + 3;
      if (now < dropTail) {
        extraWaitMs = Math.max(0, (dropTail - now) * 1000);
      }
    }

    // Beat-match: nudge incoming rate so BPMs align.
    const outgoingBpm = (currentTrack?.bpm || 120) *
      (currentRef.current && currentRef.current.getPitch ? currentRef.current.getPitch() : 1);
    const incomingBpm = next.bpm || 120;
    let ratio = outgoingBpm / incomingBpm;
    ratio = Math.max(0.85, Math.min(1.15, ratio));
    const bpmDelta = Math.round(incomingBpm * ratio - incomingBpm);
    otherSetter(next);

    const dropNotice = extraWaitMs > 0
      ? ` · waiting ${Math.ceil(extraWaitMs / 1000)}s past the drop`
      : "";
    toast(
      `Auto-DJ · ${next.title}` +
        (Math.abs(bpmDelta) >= 1 ? ` · beat-matched ${bpmDelta >= 0 ? "+" : ""}${bpmDelta} BPM` : "") +
        dropNotice,
      { duration: 3500 }
    );

    // Wait for drop-tail (if any) + a beat for the incoming player to load
    const startDelay = extraWaitMs + 1200;
    setTimeout(() => {
      try { otherRef.current && otherRef.current.setRate && otherRef.current.setRate(ratio); } catch (_) {}
      try { otherRef.current && otherRef.current.play(); } catch (_) {}

      const target = otherSide === "B" ? 1 : 0;
      const start = fader;
      const msLeft = Math.max(4000, (secondsLeft * 1000) - extraWaitMs);
      const totalMs = Math.max(5000, Math.min(15000, msLeft));

      // Screen-lock resilience: setInterval is throttled aggressively when
      // the tab is hidden (down to 1 Hz). setTimeout chains fare better and
      // fewer/larger steps ensure the full crossfade completes on time
      // even when the page is backgrounded on the lock screen.
      const steps = 15;
      const stepMs = Math.round(totalMs / steps);
      let i = 0;
      const scheduleStep = () => {
        setTimeout(() => {
          i++;
          const t = i / steps;
          const v = start + (target - start) * t;
          setFader(Math.max(0, Math.min(1, v)));
          if (i < steps) {
            scheduleStep();
          } else {
            // Crossfade complete — smoothly restore the incoming deck's
            // playback rate to its natural 1.0x over ~4s.
            if (Math.abs(ratio - 1) > 0.001 && otherRef.current && otherRef.current.setRate) {
              const restoreSteps = 8;
              const restoreMs = 4000;
              let j = 0;
              const startRate = ratio;
              const restoreStep = () => {
                setTimeout(() => {
                  j++;
                  const u = j / restoreSteps;
                  const nextRate = startRate + (1 - startRate) * u;
                  try { otherRef.current.setRate(nextRate); } catch (_e1) { /* rate control unavailable */ }
                  if (j < restoreSteps) {
                    restoreStep();
                  } else {
                    try { otherRef.current.setRate(1); } catch (_e2) { /* rate control unavailable */ }
                  }
                }, restoreMs / restoreSteps);
              };
              restoreStep();
            }
            autoTransitioning.current = false;
          }
        }, stepMs);
      };
      scheduleStep();
    }, startDelay);
  };

  const handleEnd = (side) => {
    if (autoDJ) return; // auto handled by nearEnd
  };

  return (
    <div className="space-y-4">
      {/* YouTube Premium banner (dismissible) */}
      {!dismissedYtBanner && (
        <div
          className="relative rounded-xl border p-3 sm:p-4 flex items-start gap-3"
          style={{
            background: "linear-gradient(135deg, rgba(239,68,68,0.06), rgba(217,70,239,0.06))",
            borderColor: "rgba(239,68,68,0.3)",
          }}
          data-testid="yt-premium-banner"
        >
          <div
            className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: "#ef4444" }}
          >
            <Youtube className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-heading font-bold text-white leading-tight">
              Use YouTube Premium for ad-free, high-quality streams
            </div>
            <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
              Sign in to your YouTube account in this browser once — the player will automatically
              use your session (including Premium if you have it).{" "}
              <button
                type="button"
                onClick={() => {
                  const w = window.open(
                    "https://accounts.google.com/ServiceLogin?service=youtube&continue=https%3A%2F%2Fwww.youtube.com%2F",
                    "_blank",
                    "noopener,noreferrer"
                  );
                  toast(
                    "Sign in in the new tab, then switch back to House Party — you don't need to close it.",
                    { duration: 5000 }
                  );
                  if (!w) {
                    toast.error("Popup blocked. Allow popups or long-press the link to open in a new tab.");
                  }
                }}
                className="underline text-white hover:text-fuchsia-400"
                data-testid="yt-signin-link"
              >
                Open YouTube sign-in (new tab) →
              </button>
            </div>
          </div>
          <button
            onClick={dismissYtBanner}
            className="p-1 rounded text-zinc-500 hover:text-white hover:bg-white/10 shrink-0"
            data-testid="dismiss-yt-banner"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-800">
            <div
              className="w-2 h-2 rounded-full"
              style={{
                background: wakeOn ? "#eab308" : "#52525b",
                boxShadow: wakeOn ? "0 0 8px #eab308" : "none",
              }}
              data-testid="wake-lock-indicator"
            />
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">
              {wakeOn ? "WAKE ON" : "WAKE OFF"}
            </span>
            <Switch
              checked={wakeOn}
              onCheckedChange={setWakeOn}
              className="scale-75 data-[state=checked]:bg-yellow-500"
              data-testid="wake-lock-switch"
            />
          </div>
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-800"
            title="Forces desktop viewport so both decks can play audio at the same time on mobile browsers."
          >
            <Monitor
              className="w-3.5 h-3.5"
              style={{ color: desktopMode ? "#22c55e" : "#52525b" }}
            />
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">
              DESKTOP EMU
            </span>
            <Switch
              checked={desktopMode}
              onCheckedChange={(v) => {
                setDesktopMode(v);
                toast(
                  v
                    ? "Desktop emulation ON — both decks can thump together."
                    : "Desktop emulation OFF — mobile browsers may mute the idle deck."
                );
              }}
              className="scale-75 data-[state=checked]:bg-green-500"
              data-testid="desktop-emu-switch"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-800">
          <Zap className="w-3.5 h-3.5" style={{ color: autoDJ ? "#eab308" : "#71717a" }} />
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">AUTO-DJ</span>
          <Switch
            checked={autoDJ}
            onCheckedChange={setAutoDJ}
            className="scale-75 data-[state=checked]:bg-yellow-500"
            data-testid="auto-dj-switch"
          />
        </div>
      </div>

      {/* Decks — always side-by-side, compact */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <Deck
          ref={deckARef}
          side="A"
          track={deckA}
          faderVolume={volA}
          onNearEnd={handleNearEnd}
          onEnd={handleEnd}
          onBpmChange={handleBpmChange}
          onMarkDropEnd={handleMarkDropEnd}
        />
        <Deck
          ref={deckBRef}
          side="B"
          track={deckB}
          faderVolume={volB}
          onNearEnd={handleNearEnd}
          onEnd={handleEnd}
          onBpmChange={handleBpmChange}
          onMarkDropEnd={handleMarkDropEnd}
        />
      </div>

      {/* Crossfader — full-width below both decks */}
      <div className="rounded-2xl bg-zinc-950/70 border border-zinc-800 px-3 py-3 sm:px-6 sm:py-4">
        <div className="flex justify-between mb-1.5">
          <span className="font-mono text-[10px] tracking-widest" style={{ color: "#d946ef" }}>◄ DECK A</span>
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">CROSSFADER</span>
          <span className="font-mono text-[10px] tracking-widest" style={{ color: "#06b6d4" }}>DECK B ►</span>
        </div>
        <input
          type="range"
          className="fader w-full"
          min="0" max="1" step="0.01"
          value={fader}
          onChange={(e) => setFader(parseFloat(e.target.value))}
          data-testid="crossfader"
        />
      </div>

      {/* Track drawer below decks */}
      <TrackDrawer
        tracks={tracks}
        onLoadDeck={onLoadDeck}
        deckA={deckA}
        deckB={deckB}
      />
    </div>
  );
}
